import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// --- Per-tab impersonation (super-admin quick-login) ---
// Stored in sessionStorage so each browser tab holds its OWN identity. When present, this tab
// sends it as a Bearer token (which the backend prefers over the shared admin cookie), letting
// you run different users in different tabs simultaneously.
export const IMP_KEY = "e650_imp_token";
export const isImpersonating = () => Boolean(sessionStorage.getItem(IMP_KEY));
export const getImpToken = () => sessionStorage.getItem(IMP_KEY);
export const setImpToken = (t) => sessionStorage.setItem(IMP_KEY, t);
export const clearImpToken = () => sessionStorage.removeItem(IMP_KEY);

// --- Per-tab sign-in sandbox ---
// Opened via /student?sandbox=1: this tab ignores the shared admin cookie entirely, so staff can
// mimic a student's full sign-in/out experience without touching their own session.
export const SANDBOX_KEY = "e650_sandbox";
try {
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("sandbox") === "1") {
    sessionStorage.setItem(SANDBOX_KEY, "1");
  }
} catch (_) {}
export const isSandbox = () => sessionStorage.getItem(SANDBOX_KEY) === "1";

api.interceptors.request.use((config) => {
  const t = getImpToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  if (isSandbox()) config.headers["X-Sandbox"] = "1";
  return config;
});

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
}

// --- Auto token refresh on 401 (single-flight) ---
let refreshPromise = null;

export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export function redirectToLogin() {
  if (typeof window === "undefined") return;
  const target = isSandbox() ? "/student" : "/login";
  if (!window.location.pathname.startsWith(target)) window.location.assign(target);
}

// fetch() wrapper (for multipart/file uploads) that auto-refreshes on 401 and retries once.
export async function apiFetch(url, options = {}) {
  const opts = { credentials: "include", ...options };
  const t = getImpToken();
  if (t) opts.headers = { ...(opts.headers || {}), Authorization: `Bearer ${t}` };
  let res = await fetch(url, opts);
  if (res.status === 401) {
    if (isImpersonating() || isSandbox()) { clearImpToken(); redirectToLogin(); throw new Error("Session ended"); }
    try {
      await refreshSession();
      res = await fetch(url, opts);
    } catch (e) {
      redirectToLogin();
      throw e;
    }
  }
  return res;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    const isAuthCall = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/token-login") || url.includes("/auth/student-login") || url.includes("/auth/student-forgot-id");
    const isSessionCheck = url.includes("/auth/me");

    if (status === 401 && original && !original._retry && !isAuthCall) {
      // Impersonation/sandbox tabs have no refresh cookie for their user; a 401 means the
      // session ended (revoked/expired) or the sandbox tab is simply signed out.
      if (isImpersonating() || isSandbox()) {
        clearImpToken();
        if (!isSessionCheck) redirectToLogin();
        return Promise.reject(error);
      }
      original._retry = true;
      try {
        await refreshSession();
        return api(original);
      } catch (e) {
        if (!isSessionCheck) redirectToLogin();
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

// Finalize runs as a background job on the server (big videos outlive proxy request
// limits) — poll until it lands or fails.
export async function pollFinalizeJob(jobId, { maxMinutes = 30 } = {}) {
  const deadline = Date.now() + maxMinutes * 60 * 1000;
  let misses = 0;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 3000));
    let job;
    try {
      ({ data: job } = await api.get(`/admin/uploads/finalize-status/${jobId}`));
      misses = 0;
    } catch {
      if (++misses >= 10) throw new Error("Lost contact with the server while processing the video — check the lesson in a minute before re-uploading.");
      continue;
    }
    if (job.state === "done") return job.video;
    if (job.state === "error") throw new Error(job.error || "Video processing failed — please try again.");
  }
  throw new Error("Video processing is taking unusually long — check the lesson in a few minutes before re-uploading.");
}
